import { PlanType } from "@brookbush/database";
import {
	type PlanConfigExpanded,
	PriceLookupKey,
	TrialLength,
} from "@brookbush/types";

import { addDays, addMonths, addWeeks } from "date-fns";
import { CreditCardIcon, LandmarkIcon } from "lucide-react";
import {
	SiAfterpay,
	SiAlipay,
	SiAmericanexpress,
	SiCashapp,
	SiDiscover,
	SiJcb,
	SiKlarna,
	SiMastercard,
	SiPaypal,
	SiRevolut,
	SiVisa,
	SiWechat,
} from "react-icons/si";
import type Stripe from "stripe";

export const getPaymentMethodIdentifier = (
	paymentMethod: Stripe.PaymentMethod,
) => {
	let identifier = "";

	switch (paymentMethod.type) {
		case "card":
			identifier = `Card Ending In ${paymentMethod.card?.last4}`;
			break;
		case "acss_debit":
			identifier = `ACSS Debit Payment`;
			break;
		case "afterpay_clearpay":
			identifier = `Afterpay/Clearpay Payment`;
			break;
		case "alipay":
			identifier = `Alipay Payment`;
			break;
		case "au_becs_debit":
			identifier = `AU BECS Debit Payment`;
			break;
		case "bacs_debit":
			identifier = `BACS Debit Payment`;
			break;
		case "bancontact":
			identifier = `Bancontact Payment`;
			break;
		case "blik":
			identifier = `BLIK Payment`;
			break;
		case "boleto":
			identifier = `Boleto Payment`;
			break;
		case "eps":
			identifier = `EPS Payment`;
			break;
		case "fpx":
			identifier = `FPX Payment`;
			break;
		case "giropay":
			identifier = `Giropay Payment`;
			break;
		case "grabpay":
			identifier = `GrabPay Payment`;
			break;
		case "ideal":
			identifier = `iDEAL Payment`;
			break;
		case "klarna":
			identifier = `Klarna Payment`;
			break;
		case "konbini":
			identifier = `Konbini Payment`;
			break;
		case "link":
			identifier = `Link Payment`;
			break;
		case "oxxo":
			identifier = `OXXO Payment`;
			break;
		case "p24":
			identifier = `P24 Payment`;
			break;
		case "paynow":
			identifier = `PayNow Payment`;
			break;
		case "pix":
			identifier = `Pix Payment`;
			break;
		case "promptpay":
			identifier = `PromptPay Payment`;
			break;
		case "sepa_debit":
			identifier = `SEPA Debit ending in ${paymentMethod.sepa_debit?.last4}`;
			break;
		case "sofort":
			identifier = `Sofort Payment`;
			break;
		case "us_bank_account":
			identifier = `US Bank Account ending in ${paymentMethod.us_bank_account?.last4}`;
			break;
		case "wechat_pay":
			identifier = `WeChat Pay Payment`;
			break;
		case "paypal":
			identifier = `PayPal Payment`;
			break;
		case "affirm":
			identifier = `Affirm Payment`;
			break;
		case "customer_balance":
			identifier = `Customer Balance Payment`;
			break;

		case "zip":
			identifier = `Zip Payment`;
			break;

		case "cashapp":
			identifier = `Cash App ${paymentMethod.cashapp?.cashtag}`;
			break;

		case "amazon_pay":
			identifier = "Amazon Pay";
			break;

		case "revolut_pay":
			identifier = "Revolut Pay";
			break;

		default:
			identifier = `${paymentMethod.type} Payment`;
	}

	return identifier;
};

export const getPaymentMethodIcon = (paymentMethod: Stripe.PaymentMethod) => {
	let icon = null;

	switch (paymentMethod.type) {
		case "card": {
			const brand = paymentMethod.card?.brand;

			switch (brand) {
				case "visa":
					icon = SiVisa;
					break;
				case "mastercard":
					icon = SiMastercard;
					break;
				case "amex":
					icon = SiAmericanexpress;
					break;

				case "discover":
					icon = SiDiscover;
					break;

				case "unionpay":
					icon = SiJcb;
					break;

				default:
					icon = CreditCardIcon;
					break;
			}

			break;
		}
		case "acss_debit":
			icon = LandmarkIcon;
			break;
		case "afterpay_clearpay":
			icon = SiAfterpay;
			break;
		case "alipay":
			icon = SiAlipay;
			break;
		case "au_becs_debit":
			icon = LandmarkIcon;
			break;
		case "bacs_debit":
			icon = LandmarkIcon;
			break;
		case "bancontact":
			icon = LandmarkIcon;
			break;

		case "klarna":
			icon = SiKlarna;
			break;

		case "sepa_debit":
			icon = LandmarkIcon;
			break;

		case "us_bank_account":
			icon = LandmarkIcon;
			break;

		case "wechat_pay":
			icon = SiWechat;

			break;
		case "paypal":
			icon = SiPaypal;

			break;

		case "cashapp":
			icon = SiCashapp;
			break;

		case "amazon_pay":
			icon = CreditCardIcon;
			break;

		case "revolut_pay":
			icon = SiRevolut;
			break;

		default:
			icon = CreditCardIcon;
	}

	return icon;
};

export const getBillingInterval = (price: Stripe.Price) =>
	price.recurring?.interval!;

export const formatPrice = (obj: {
	currency?: string | null;
	value: number;
	hideCurrencySymbol?: boolean;
}) => {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: obj?.currency ?? "usd",
		trailingZeroDisplay: "stripIfInteger",

		currencyDisplay: obj.hideCurrencySymbol ? "narrowSymbol" : "symbol",
	}).format(obj.value);
};

export const isSamePlan = (
	firstPrice?: Stripe.Price | null,
	secondPrice?: Stripe.Price | null,
) =>
	!!firstPrice &&
	!!secondPrice &&
	firstPrice.billing_scheme === secondPrice.billing_scheme &&
	getBillingInterval(firstPrice) === getBillingInterval(secondPrice);

export const getAmountIndividual = (price: Stripe.Price) =>
	convertToDollars(price.unit_amount!);

export const getAmountIndividualAfterDiscount = (
	price: Stripe.Price,
	coupon: Stripe.Coupon,
) =>
	+(
		coupon.amount_off
			? getAmountIndividual(price) - convertToDollars(coupon.amount_off)
			: (getAmountIndividual(price) * (100 - coupon.percent_off!)) / 100
	).toFixed(2);

export const isFormerMemberRate = (price: Stripe.Price) =>
	price?.unit_amount === PriceLookupKey.STRIPE_OLD_MONTHLY_PLAN_AMOUNT ||
	price?.unit_amount === PriceLookupKey.STRIPE_OLD_YEARLY_PLAN_AMOUNT;

export const getOriginalRateFromOldPrice = (price: Stripe.Price) =>
	price?.unit_amount === PriceLookupKey.STRIPE_OLD_MONTHLY_PLAN_AMOUNT
		? 29.99
		: price?.unit_amount === PriceLookupKey.STRIPE_OLD_YEARLY_PLAN_AMOUNT
			? 299
			: 0;

// Following are for team memberships

export const getPriceTier = (price: Stripe.Price, quantity: number) =>
	price.tiers!.find((tier) => quantity <= tier.up_to!)!;

export const getAmountPerSeat = (price: Stripe.Price, quantity: number) =>
	convertToDollars(getPriceTier(price, quantity).unit_amount!);

export const getAmountPerSeatAfterDiscount = (
	price: Stripe.Price,
	quantity: number,
	coupon: Stripe.Coupon,
) =>
	+(
		coupon.amount_off
			? getAmountPerSeat(price, quantity) -
				convertToDollars(coupon.amount_off) / quantity
			: (getAmountPerSeat(price, quantity) * (100 - coupon.percent_off!)) / 100
	).toFixed(2);

export const convertToDollars = (amount: number) => +(amount / 100).toFixed(2);
export const convertToCents = (amount: number) => +(amount * 100).toFixed(0);

export const getFirstBillingDate = (trialLength: TrialLength) =>
	(trialLength === TrialLength.MONTH
		? addMonths(new Date(), 1)
		: trialLength === TrialLength.WEEK
			? addWeeks(new Date(), 1)
			: addDays(new Date(), 1)
	).toDateString();

/**
 * Amount charged today in dollars. `joiningFee` is added to individual plans
 * only and is never discounted; trials owe nothing today.
 *
 * @param {PlanConfigExpanded} planConfig - Current plan selection
 * @param {number} [joiningFee] - Joining-price fee in dollars
 * @returns {number} Amount due today
 */
export const getTotalAmountDue = (
	planConfig: PlanConfigExpanded,
	joiningFee = 0,
) =>
	planConfig.trialLength
		? 0
		: planConfig.planType === PlanType.INDIVIDUAL
			? +(
					(planConfig.promoCode
						? getAmountIndividualAfterDiscount(
								planConfig.price!,
								planConfig.promoCode!.coupon,
							)
						: getAmountIndividual(planConfig.price!)) + joiningFee
				).toFixed(2)
			: planConfig.promoCode
				? getAmountPerSeatAfterDiscount(
						planConfig.price!,
						planConfig.quantity,
						planConfig.promoCode!.coupon,
					)! * planConfig.quantity
				: +(
						getAmountPerSeat(planConfig.price!, planConfig.quantity)! *
						planConfig.quantity
					).toFixed(2);
